import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Clock, RefreshCw, X, ChevronRight } from 'lucide-react';
import api from '../api';
import VerificationPanel, { } from '../components/Verification/VerificationPanel';

interface Stats {
  total_traces: number;
  needing_human_review: number;
  pending_escalations: number;
  by_module: Array<{
    module: string;
    count: string;
    avg_failure_rate: string;
    avg_confidence: string;
    avg_latency_ms: string;
    total_token_overhead: string;
  }>;
}

interface Escalation {
  id: number;
  trace_id: string;
  module: string;
  project_id: number | null;
  failure_rate: string;
  flagged_claims: number;
  status: string;
  created_at: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  trace?: any;
}

const VerificationDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEsc, setSelectedEsc] = useState<Escalation | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'in_review' | 'resolved' | 'dismissed'>('pending');
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, escalationsRes] = await Promise.all([
        api.get('/api/verification/stats'),
        api.get('/api/verification/escalations', {
          params: statusFilter === 'all' ? {} : { status: statusFilter }
        })
      ]);
      setStats(statsRes.data.stats);
      setEscalations(escalationsRes.data.escalations || []);
    } catch (err) {
      console.error('Failed to load verification dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const updateEscalation = async (id: number, status: string) => {
    setActionLoading(true);
    try {
      await api.patch(`/api/verification/escalations/${id}`, {
        status,
        review_notes: reviewNotes || undefined
      });
      setReviewNotes('');
      setSelectedEsc(null);
      await loadData();
    } catch (err) {
      console.error('Failed to update escalation:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatModule = (m: string) => m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            Verification Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Quality review queue for AI-generated outputs flagged by self-verification</p>
        </div>
        <div className="flex items-center gap-2">
          {(stats?.pending_escalations ?? 0) > 0 && (
            <button
              onClick={() => setStatusFilter('pending')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
            >
              <AlertTriangle className="w-4 h-4" />
              Review Pending ({stats?.pending_escalations})
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase">Total Verifications</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats?.total_traces ?? '—'}</div>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-4">
          <div className="text-xs text-amber-700 uppercase">Need Human Review</div>
          <div className="text-2xl font-bold text-amber-700 mt-1 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {stats?.needing_human_review ?? '—'}
          </div>
        </div>
        <button
          onClick={() => setStatusFilter('pending')}
          className="bg-white border border-red-200 rounded-lg p-4 text-left hover:bg-red-50 transition-colors w-full"
        >
          <div className="text-xs text-red-700 uppercase">Pending Escalations</div>
          <div className="text-2xl font-bold text-red-700 mt-1 flex items-center justify-between">
            {stats?.pending_escalations ?? '—'}
            <ChevronRight className="w-5 h-5 text-red-400" />
          </div>
        </button>
      </div>

      {stats?.by_module && stats.by_module.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Per Module</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left py-2">Module</th>
                <th className="text-right py-2">Runs</th>
                <th className="text-right py-2">Avg Failure Rate</th>
                <th className="text-right py-2">Avg Confidence</th>
                <th className="text-right py-2">Avg Latency</th>
                <th className="text-right py-2">Total Tokens</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_module.map((m, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 font-medium">{formatModule(m.module)}</td>
                  <td className="text-right">{m.count}</td>
                  <td className="text-right">{(parseFloat(m.avg_failure_rate || '0') * 100).toFixed(1)}%</td>
                  <td className="text-right">{parseFloat(m.avg_confidence || '0').toFixed(0)}</td>
                  <td className="text-right">{parseFloat(m.avg_latency_ms || '0').toFixed(0)}ms</td>
                  <td className="text-right">{parseInt(m.total_token_overhead || '0', 10).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Escalations</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="pending">Pending</option>
            <option value="in_review">In Review</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
            <option value="all">All</option>
          </select>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : escalations.length === 0 ? (
          <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-2">
            <CheckCircle className="w-8 h-8 text-green-600" />
            No escalations matching this filter
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {escalations.map(esc => (
              <li
                key={esc.id}
                className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-4"
                onClick={() => setSelectedEsc(esc)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">{formatModule(esc.module)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      esc.status === 'pending' ? 'bg-red-100 text-red-700' :
                      esc.status === 'in_review' ? 'bg-amber-100 text-amber-700' :
                      esc.status === 'resolved' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{esc.status}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(esc.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-900">
                    {esc.flagged_claims} claim(s) flagged · failure rate {(parseFloat(esc.failure_rate) * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1">{esc.trace_id}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedEsc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Escalation #{selectedEsc.id}</h3>
                <p className="text-xs text-gray-500">{formatModule(selectedEsc.module)} · {selectedEsc.flagged_claims} flagged claims</p>
              </div>
              <button onClick={() => { setSelectedEsc(null); setReviewNotes(''); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedEsc.trace ? (
                <>
                  <div className="mb-3 flex justify-end">
                    <a
                      href={`/api/verification/traces/${selectedEsc.trace_id}/appendix?deliverable_title=${encodeURIComponent(selectedEsc.module)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Download AI Accuracy Appendix (PDF)
                    </a>
                  </div>
                  <VerificationPanel metadata={selectedEsc.trace} embedded />
                </>
              ) : (
                <div className="text-sm text-gray-500">Trace data not available.</div>
              )}
            </div>
            {(selectedEsc.status === 'pending' || selectedEsc.status === 'in_review') && (
              <div className="p-4 border-t border-gray-200 space-y-3">
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Review notes (optional)"
                  rows={2}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => updateEscalation(selectedEsc.id, 'in_review')}
                    disabled={actionLoading || selectedEsc.status === 'in_review'}
                    className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50"
                  >
                    Mark In Review
                  </button>
                  <button
                    onClick={() => updateEscalation(selectedEsc.id, 'dismissed')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => updateEscalation(selectedEsc.id, 'resolved')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationDashboard;
